/**
 * عروضي — template renderer. Draws ONE design as an exact-size PNG on a
 * native <canvas> (no html-to-image / canvas library; `canvas` is a devDep
 * used only so Jest can check real pixels).
 *
 *   lshape  1920x1080  full-bleed. The player stretches this ONE image to the
 *                      screen and windows it: only the right strip (384x864)
 *                      and the bottom strip (1920x216) are ever visible; the
 *                      top-left 1536x864 sits under the running content.
 *   bottom  1920x216   the bar, CENTER_CROP into a 1920x216 box (exact fit).
 *   corner  400x400    transparent PNG, FIT_CENTER into a square.
 *
 * Layout values come from .claude/promos-prototype.html (stage CSS). The
 * editor's live preview paints THIS canvas (via ShapeFrame), so preview and
 * uploaded PNG cannot drift apart.
 *
 * Arabic: ctx.direction = 'rtl' on every draw; fonts must be loaded first
 * (loadFonts) or the canvas silently falls back to a default face.
 */
import QRCode from 'qrcode';
import { PALETTES, SHAPE_SIZE, paletteById, currencyByCode, isLightPalette } from './constants';
import { drawRiyal, riyalSize } from './riyal';

export const DISPLAY_FONT = 'Cairo';
export const UI_FONT = '"IBM Plex Sans Arabic"';
const disp = (w, s) => `${w} ${s}px ${DISPLAY_FONT}, ${UI_FONT}, system-ui, sans-serif`;
const ui = (w, s) => `${w} ${s}px ${UI_FONT}, system-ui, sans-serif`;

const QR_PAPER = '#FFFDF8';
const QR_INK = '#1A120A';
const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
export const arDigits = (s) => String(s).replace(/\d/g, (d) => AR_DIGITS[d]);

/* ---------------------------------------------------------------- fonts */

/** Every face/weight the renderer uses. document.fonts.load(font, text) also pulls the unicode-range subset that covers `text`. */
export const FONT_FACES = [
  ['900 64px Cairo'], ['700 28px Cairo'],
  ['400 28px "IBM Plex Sans Arabic"'], ['500 28px "IBM Plex Sans Arabic"'],
  ['600 28px "IBM Plex Sans Arabic"'], ['700 28px "IBM Plex Sans Arabic"'],
];

export async function loadFonts(texts = [], fontSet = typeof document !== 'undefined' ? document.fonts : null) {
  if (!fontSet || typeof fontSet.load !== 'function') return false;
  const sample = `${texts.join(' ')} اأبتثجحخدذرزسشصضطظعغفقكلمنهوي٠١٢٣٤٥٦٧٨٩ 0123456789`;
  try {
    await Promise.all(FONT_FACES.map(([f]) => fontSet.load(f, sample)));
    if (fontSet.ready) await fontSet.ready;
    return true;
  } catch (e) {
    return false;
  }
}

/** CORS-clean image load (crossOrigin=anonymous) so toBlob() is not blocked by a tainted canvas. */
export function loadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image load failed: ${url}`));
    img.src = url;
  });
}

/* ------------------------------------------------------------ primitives */

function newCanvas(w, h, createCanvas) {
  if (createCanvas) return createCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

function rr(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.arcTo(x + w, y, x + w, y + rad, rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.arcTo(x + w, y + h, x + w - rad, y + h, rad);
  ctx.lineTo(x + rad, y + h);
  ctx.arcTo(x, y + h, x, y + h - rad, rad);
  ctx.lineTo(x, y + rad);
  ctx.arcTo(x, y, x + rad, y, rad);
  ctx.closePath();
}

const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const mixWhite = (hex, keep) => {
  const n = parseInt(hex.slice(1), 16);
  const m = (c) => Math.round(c * keep + 255 * (1 - keep));
  return `rgb(${m((n >> 16) & 255)},${m((n >> 8) & 255)},${m(n & 255)})`;
};

function setText(ctx, font, color, align) {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.direction = 'rtl';
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
}

const tw = (ctx, font, text) => { ctx.font = font; return ctx.measureText(text).width; };

/** Greedy word wrap into at most maxLines; the last line is ellipsised if text still overflows. */
function wrap(ctx, font, text, maxW, maxLines) {
  ctx.font = font;
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width <= maxW || !cur) cur = next; else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = ellipsize(ctx, font, lines.slice(maxLines - 1).join(' '), maxW);
    return kept;
  }
  return lines.map((l) => (ctx.measureText(l).width > maxW ? ellipsize(ctx, font, l, maxW) : l));
}

function ellipsize(ctx, font, text, maxW) {
  ctx.font = font;
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

/** Largest size in [min,start] (step 2) where `text` fits on one line; ellipsises at min. */
function fitOneLine(ctx, make, text, maxW, start, min) {
  for (let s = start; s >= min; s -= 2) if (tw(ctx, make(s), text) <= maxW) return { size: s, text };
  return { size: min, text: ellipsize(ctx, make(min), text, maxW) };
}

function coverDraw(ctx, img, x, y, w, h) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const s = Math.max(w / iw, h / ih);
  const dw = iw * s; const dh = ih * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

function containDraw(ctx, img, x, y, w, h) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const s = Math.min(w / iw, h / ih);
  const dw = iw * s; const dh = ih * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/* ------------------------------------------------------------- backdrops */

function paintBackdrop(ctx, p, W, H) {
  // linear-gradient(160deg, bg1, bg2)
  const ang = (160 * Math.PI) / 180;
  const dx = Math.sin(ang); const dy = -Math.cos(ang);
  const len = Math.abs(W * Math.sin(ang)) + Math.abs(H * Math.cos(ang));
  const cx = W / 2; const cy = H / 2;
  const g = ctx.createLinearGradient(cx - (dx * len) / 2, cy - (dy * len) / 2, cx + (dx * len) / 2, cy + (dy * len) / 2);
  g.addColorStop(0, p.bg1); g.addColorStop(1, p.bg2);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  // radial-gradient(900px 600px at 1700px 900px, a 28% -> transparent 70%)
  ctx.save();
  ctx.translate(1700, 900); ctx.scale(1.5, 1);
  const r = ctx.createRadialGradient(0, 0, 0, 0, 0, 600);
  r.addColorStop(0, hexA(p.a, 0.28)); r.addColorStop(0.7, hexA(p.a, 0));
  ctx.fillStyle = r; ctx.fillRect(-1200, -900, 2400, 1800);
  ctx.restore();
}

/* ---------------------------------------------------------------- atoms */

/** Product disc: photo (cover, clipped) or the palette gradient with `fallback()` drawn on it. */
function drawProduct(ctx, p, cx, cy, d, photo, fallback) {
  const r = d / 2;
  ctx.save();
  ctx.shadowColor = isLightPalette(p) ? 'rgba(60,40,20,0.28)' : 'rgba(0,0,0,0.6)'; ctx.shadowBlur = Math.round(d * 0.2); ctx.shadowOffsetY = Math.round(d * 0.08);
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
  const g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.4, 0, cx - r * 0.3, cy - r * 0.4, r * 1.5);
  g.addColorStop(0, mixWhite(p.a, 0.6)); g.addColorStop(1, p.a2);
  ctx.fillStyle = g; ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
  if (photo) coverDraw(ctx, photo, cx - r, cy - r, d, d); else if (fallback) fallback(cx, cy, r);
  ctx.restore();
  if (photo) {
    ctx.beginPath(); ctx.arc(cx, cy, r - 3, 0, Math.PI * 2);
    ctx.lineWidth = 6; ctx.strokeStyle = p.a; ctx.stroke();
  }
}

/** Height of the numerals as drawn (the Lam of the riyal symbol aligns with it, SAMA vertical-alignment rule). */
function digitHeight(ctx, font, text, size) {
  ctx.font = font;
  const m = ctx.measureText(text);
  return Math.round(m.actualBoundingBoxAscent || size * 0.7);
}

/** SAR shows SAMA's official symbol (left of the numeral, one-third-height clear space); other currencies keep their text. */
const usesRiyalSymbol = (cur) => cur && cur.code === 'SAR';

function pillSize(ctx, price, cur, size, padX, padY) {
  const num = arDigits(price);
  const nw = tw(ctx, disp(900, size), num);
  let cw; let gap = 8;
  if (usesRiyalSymbol(cur)) {
    const sym = riyalSize(digitHeight(ctx, disp(900, size), num, size));
    cw = sym.w; gap = sym.gap;
  } else cw = tw(ctx, ui(700, Math.round(size * 0.45)), cur.sym);
  return { w: Math.ceil(nw + gap + cw + padX * 2), h: Math.round(size * 1.25 + padY * 2) };
}

/** Price pill. `anchor` = {right}|{cx}; returns {x,w,h}. */
function drawPill(ctx, p, anchor, top, price, cur, size, padX, padY) {
  const numFont = disp(900, size);
  const num = arDigits(price);
  const { w, h } = pillSize(ctx, price, cur, size, padX, padY);
  const x = anchor.right !== undefined ? anchor.right - w : anchor.cx - w / 2;
  ctx.fillStyle = p.a; rr(ctx, x, top, w, h, h / 2); ctx.fill();
  const base = top + padY + size * 1.0;
  setText(ctx, numFont, p.onA, 'right'); ctx.fillText(num, x + w - padX, base);
  if (usesRiyalSymbol(cur)) {
    drawRiyal(ctx, x + padX, base, digitHeight(ctx, numFont, num, size), p.onA); // coloured like the price, at the price's digit height
  } else {
    setText(ctx, ui(700, Math.round(size * 0.45)), p.onA, 'left'); ctx.fillText(cur.sym, x + padX, base);
  }
  return { x, w, h };
}

/** Simple cup glyph (stroke only) used when a product disc has no photo and no logo. */
function drawCup(ctx, p, cx, cy, r) {
  const u = r * 0.56 / 7;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.strokeStyle = p.onA; ctx.lineWidth = u * 1.1; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(-6 * u, -2 * u); ctx.lineTo(5 * u, -2 * u); ctx.lineTo(4 * u, 7 * u);
  ctx.lineTo(-5 * u, 7 * u); ctx.closePath(); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(5 * u, 0); ctx.lineTo(7 * u, 0);
  ctx.arc(7 * u, 2.5 * u, 2.5 * u, -Math.PI / 2, Math.PI / 2); ctx.lineTo(4.5 * u, 5 * u); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-3 * u, -5 * u); ctx.quadraticCurveTo(-2 * u, -7 * u, -3 * u, -9 * u);
  ctx.moveTo(1 * u, -5 * u); ctx.quadraticCurveTo(2 * u, -7 * u, 1 * u, -9 * u); ctx.stroke();
  ctx.restore();
}

/**
 * QR card: paper-coloured rounded square, error correction M, NO centre badge (a badge costs scannability and
 * H-level density). Whole-pixel cells, a quiet zone of 2 modules on every side, centred in the card.
 * cell = floor(size / (modules + 4)): the largest whole-pixel cell the card allows with a 2-module quiet zone.
 * Returns the geometry so tests/reports can state version, modules and px per module.
 */
export function qrGeometry(text, size = 210) {
  const qrObj = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const n = qrObj.modules.size;
  const cell = Math.max(1, Math.floor(size / (n + 4)));
  return { version: qrObj.version, modules: n, cell, codePx: n * cell, symbolPx: (n + 4) * cell, matrix: qrObj.modules };
}

function drawQr(ctx, p, qr, x, y, size, radius) {
  const g = qrGeometry(qr.text, size);
  ctx.fillStyle = QR_PAPER; rr(ctx, x, y, size, size, radius); ctx.fill();
  if (isLightPalette(p)) { rr(ctx, x - 1, y - 1, size + 2, size + 2, radius + 1); ctx.lineWidth = 2; ctx.strokeStyle = hexA(p.dim, 0.55); ctx.stroke(); } // the outline sits OUTSIDE the card: the card stays exactly 210
  const ox = x + Math.round((size - g.codePx) / 2);
  const oy = y + Math.round((size - g.codePx) / 2);
  ctx.fillStyle = QR_INK;
  for (let r = 0; r < g.modules; r++) for (let c = 0; c < g.modules; c++) if (g.matrix.get(r, c)) ctx.fillRect(ox + c * g.cell, oy + r * g.cell, g.cell, g.cell);
  return { x, y, size, ...g, matrix: undefined };
}

function captionBelow(ctx, p, text, cx, y, maxW, size) {
  if (!text) return;
  const f = ui(600, size);
  const t = ellipsize(ctx, f, text, maxW);
  setText(ctx, f, p.dim, 'center');
  ctx.fillText(t, cx, y);
}

/* ---------------------------------------------------------------- shapes */

function paintLShape(ctx, p, d) {
  const W = 1920; const H = 1080;
  paintBackdrop(ctx, p, W, H);
  ctx.fillStyle = p.a; ctx.fillRect(1536, 0, 5, 869); ctx.fillRect(0, 864, 1541, 5);

  // bottom band (x 0..1541, y 869..1080): one big line, sub || title
  const band = d.item.line || d.item.title;
  if (band) {
    const f = fitOneLine(ctx, (s) => disp(900, s), band, 1541 - 120, 72, 40);
    setText(ctx, disp(900, f.size), p.ink, 'center');
    ctx.fillText(f.text, 1541 / 2, 869 + 211 / 2 + f.size * 0.34);
  }

  // brand block (x 1541..1920, y 869..1080)
  ctx.fillStyle = p.a; ctx.fillRect(1541, 869, 379, 211);
  drawBrand(ctx, p, d.venue, 1541, 869, 379, 211, 60, d.brand);

  return paintStrip(ctx, p, d);
}

/**
 * Right strip (x 1541..1920, y 0..864), prototype v8: padding 34 30 28, gap 14, title 56/1.12, description 28/1.5,
 * the product HERO (photo disc with the price tag overlapping its bottom: 190 px with a QR, 300 px and a larger price
 * without one, centred in the free space) and the QR card (210) + link line anchored at the bottom.
 * Returns every position plus `contentBottom` / `limit`: the strip's "scrollHeight <= clientHeight" check.
 */
export function layoutLStrip(ctx, d) {
  const colW = 379 - 60; const right = 1541 + 379 - 30; const cx = 1541 + 379 / 2;
  const top = 34; const limit = 864 - 28; const GAP = 14;
  const hasQr = !!d.qr; const hasPhoto = !!d.photo; const hasPrice = !!d.item.price;
  const urlH = hasQr && d.qr.display ? 29 : 0;
  const qrH = hasQr ? 210 + (urlH ? 8 + urlH : 0) : 0;
  const qrTop = limit - qrH;
  const pc = hasQr ? { size: 48, px: 22, py: 4, over: 18 } : { size: 60, px: 28, py: 6, over: 22 };
  const pillH = Math.round(pc.size * 1.25 + pc.py * 2);
  let titleSize = 56; let subMax = 2; let heroD = hasQr ? 190 : 300;
  let m;
  for (;;) {
    const lh = Math.round(titleSize * 1.12);
    const tLines = wrap(ctx, disp(900, titleSize), d.item.title, colW, 3);
    const sLines = d.item.line ? wrap(ctx, ui(500, 28), d.item.line, colW, subMax) : [];
    const titleBottom = top + tLines.length * lh;
    const subBottom = sLines.length ? titleBottom + GAP + sLines.length * 42 : titleBottom;
    let hero = null; let pill = null; let bottomUsed = subBottom; let fits;
    if (hasPhoto) {
      if (hasQr) {
        const hTop = subBottom + GAP + 4;
        hero = { cx, cy: hTop + heroD / 2, d: heroD };
        bottomUsed = hTop + heroD + 22; // margin-bottom 22 holds the price overhang
        fits = bottomUsed <= qrTop;
      } else {
        const regTop = subBottom + GAP;
        const free = limit - regTop;
        const cyHero = regTop + (free - heroD) / 2 + heroD / 2; // margin: auto 0
        hero = { cx, cy: cyHero, d: heroD };
        bottomUsed = cyHero + heroD / 2 + pc.over;
        fits = heroD + pc.over <= free;
      }
      if (hasPrice) pill = { cx, top: hero.cy + heroD / 2 + pc.over - pillH, ...pc };
    } else if (hasPrice) {
      const regTop = subBottom + GAP;
      const free = (hasQr ? qrTop : limit) - regTop;
      const pTop = hasQr ? regTop : regTop + (free - pillH) / 2;
      pill = { cx, top: pTop, ...pc };
      bottomUsed = pTop + pillH;
      fits = pillH <= free;
    } else fits = subBottom <= (hasQr ? qrTop : limit);
    m = { tLines, sLines, lh, titleSize, hero, pill, pillH, right, cx, colW, qrTop, hasQr, contentBottom: Math.max(bottomUsed, hasQr ? limit : 0), limit, top, fits };
    if (fits) break;
    if (subMax > 1) subMax = 1;
    else if (titleSize > 40) titleSize -= 4;
    else if (hasQr && hasPhoto && heroD > 130) heroD -= 10;
    else break;
  }
  m.overflow = !m.fits;
  return m;
}

function paintStrip(ctx, p, d) {
  const m = layoutLStrip(ctx, d);
  let y = m.top;
  setText(ctx, disp(900, m.titleSize), p.ink, 'right');
  m.tLines.forEach((ln) => { ctx.fillText(ln, m.right, y + m.lh * 0.86); y += m.lh; });
  if (m.sLines.length) {
    y += 14;
    setText(ctx, ui(500, 28), p.dim, 'right');
    m.sLines.forEach((ln) => { ctx.fillText(ln, m.right, y + 30); y += 42; });
  }
  if (m.hero) drawProduct(ctx, p, m.hero.cx, m.hero.cy, m.hero.d, d.photo, null);
  if (m.pill) {
    ctx.save();
    if (m.hero) { ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 8; }
    drawPill(ctx, p, { cx: m.pill.cx }, m.pill.top, d.item.price, d.currency, m.pill.size, m.pill.px, m.pill.py);
    ctx.restore();
  }
  if (m.hasQr) {
    drawQr(ctx, p, d.qr, Math.round(m.cx - 105), Math.round(m.qrTop), 210, 22); // whole pixels: the card is exactly 210 wide
    captionBelow(ctx, p, d.qr.display, m.cx, m.qrTop + 210 + 8 + 20, m.colW + 20, 18);
  }
  return m;
}

/** Largest font size (<= start, >= min) at which `text` wraps into at most 2 lines that each fit `maxW`; ellipsised at min. */
function fitTwoLines(ctx, make, text, maxW, start, min) {
  for (let size = start; size >= min; size -= 2) {
    ctx.font = make(size);
    const words = String(text).trim().split(/\s+/).filter(Boolean);
    const lines = []; let cur = '';
    let overflow = false;
    words.forEach((w) => {
      const next = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(next).width <= maxW || !cur) cur = next; else { lines.push(cur); cur = w; }
    });
    if (cur) lines.push(cur);
    lines.forEach((l) => { if (ctx.measureText(l).width > maxW) overflow = true; });
    if (lines.length <= 2 && !overflow) return { size, lines };
  }
  return { size: min, lines: wrap(ctx, make(min), text, maxW, 2) };
}

/**
 * Café identity block.
 *   mode 'logo' : the logo inside a 160 px light disc (#FFFDF8), CONTAINED (never cropped, transparency kept), inside the disc's inscribed square
 *   mode 'name' : the café name, Cairo 900 52px/1.08, balanced, auto-shrunk to at most 2 lines
 *   undefined   : a template saved before this existed keeps its old look (logo contained in the block, or the name at nameSize)
 */
function drawBrand(ctx, p, venue, x, y, w, h, nameSize, mode) {
  const cx = x + w / 2; const cy = y + h / 2;
  if (mode === 'name') {
    const text = (venue.venueName || venue.name || '').trim();
    if (!text) return;
    const f = fitTwoLines(ctx, (s) => disp(900, s), text, w - 48, 52, 26);
    const lh = f.size * 1.08;
    let yy = cy - (f.lines.length * lh) / 2 + f.size * 0.84;
    setText(ctx, disp(900, f.size), p.onA, 'center');
    f.lines.forEach((ln) => { ctx.fillText(ln, cx, yy); yy += lh; });
    return;
  }
  if (mode === 'logo') {
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 24; ctx.shadowOffsetY = 10;
    ctx.fillStyle = QR_PAPER; ctx.beginPath(); ctx.arc(cx, cy, 80, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    if (venue.logo) containDraw(ctx, venue.logo, cx - 56, cy - 56, 112, 112); // 112 = inscribed square of the 160 disc: never cropped
    else if (venue.initial) { setText(ctx, disp(900, 72), p.a2, 'center'); ctx.fillText(venue.initial, cx, cy + 25); }
    return;
  }
  if (venue.logo) { containDraw(ctx, venue.logo, x + 40, y + 24, w - 80, h - 48); return; }
  const name = venue.name || '';
  if (!name) return;
  const lines = wrap(ctx, disp(900, nameSize), name, w - 48, 2);
  const lh = Math.round(nameSize * 1.05);
  let yy = y + h / 2 - ((lines.length * lh) / 2) + nameSize * 0.82;
  setText(ctx, disp(900, nameSize), p.onA, 'center');
  lines.forEach((ln) => { ctx.fillText(ln, x + w / 2, yy); yy += lh; });
}

function paintBar(ctx, p, d) {
  const W = 1920; const H = 216;
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, p.bg1); g.addColorStop(1, p.bg2);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = p.a; ctx.fillRect(0, 0, W, 5);
  // brand block at the right edge
  ctx.fillStyle = p.a; ctx.fillRect(W - 380, 5, 380, H - 5);
  drawBrand(ctx, p, d.venue, W - 380, 5, 380, H - 5, 64, d.brand);

  const innerR = W - 380 - 50; let cursorL = 50;
  let txRight = innerR;
  const midY = 5 + (H - 5) / 2;
  if (d.photo) { const pd = 168; drawProduct(ctx, p, txRight - pd / 2, midY, pd, d.photo, null); txRight -= pd + 30; }
  if (d.item.price) {
    const pill = pillSize(ctx, d.item.price, d.currency, 58, 26, 6);
    drawPill(ctx, p, { right: cursorL + pill.w }, midY - pill.h / 2, d.item.price, d.currency, 58, 26, 6);
    cursorL += pill.w + 36;
  }
  const maxW = txRight - cursorL;
  const t = fitOneLine(ctx, (s) => disp(900, s), d.item.title, maxW, 76, 40);
  const hasLine = !!d.item.line;
  const s = hasLine ? fitOneLine(ctx, (z) => ui(500, z), d.item.line, maxW, 30, 20) : null;
  const blockH = t.size * 1.1 + (hasLine ? 42 : 0);
  let ty = midY - blockH / 2 + t.size * 0.88;
  setText(ctx, disp(900, t.size), p.ink, 'right'); ctx.fillText(t.text, txRight, ty);
  if (hasLine) { setText(ctx, ui(500, s.size), p.dim, 'right'); ctx.fillText(s.text, txRight, ty + 44); }
}

function paintCorner(ctx, p, d) {
  const W = 400;
  ctx.clearRect(0, 0, W, W);
  const hasTitle = !!d.item.title;
  const hasPrice = !!d.item.price;
  const capH = hasTitle ? 74 : 0;
  const dia = hasPrice ? 270 : 300;
  const cy = capH + (W - capH - (hasPrice ? 34 : 0)) / 2 + (hasPrice ? 0 : 6);
  const cx = W / 2;
  if (hasTitle) {
    const f = fitOneLine(ctx, (s) => disp(900, s), d.item.title, W - 20, 54, 30);
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 16; ctx.shadowOffsetY = 3;
    setText(ctx, disp(900, f.size), '#FFFFFF', 'center');
    ctx.fillText(f.text, cx, 52);
    ctx.restore();
  }
  // The corner shows the PRODUCT photo only. Never the venue logo (the editor blocks saving without a photo; this placeholder is preview-only).
  drawProduct(ctx, p, cx, cy, dia, d.photo, (px, py, r) => drawCup(ctx, p, px, py, r));
  if (hasPrice) {
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 8;
    const pillH = Math.round(68 * 1.25 + 12);
    drawPill(ctx, p, { cx }, W - 4 - pillH, d.item.price, d.currency, 68, 28, 6);
    ctx.restore();
  }
}

/* ------------------------------------------------------------------ API */

/**
 * @param {object} d
 *   shape: 'lshape'|'bottom'|'corner'
 *   palette: palette id
 *   item: {title, line, price}
 *   currency: code ('SAR'|'JOD'|'EGP')
 *   venue: {name, initial, logo: Image|null}
 *   photo: Image|null
 *   qr: null | {text, display}   text = exact QR payload (short /q url, or WIFI: string)
 * @param {(w,h)=>Canvas} [createCanvas] injected by tests (node-canvas)
 * @returns canvas of EXACT size SHAPE_SIZE[shape]
 */
export function renderDesign(d, createCanvas) {
  const size = SHAPE_SIZE[d.shape];
  if (!size) throw new Error(`renderDesign: unsupported shape ${d.shape}`);
  const canvas = newCanvas(size.w, size.h, createCanvas);
  const ctx = canvas.getContext('2d');
  const p = paletteById(d.item?.palette || d.palette); // per-design theme, falling back to the promo-level default
  const cur = typeof d.currency === 'string' ? currencyByCode(d.currency) : d.currency;
  const dd = { ...d, item: { title: '', line: '', price: '', ...d.item }, currency: cur, venue: d.venue || {}, qr: d.shape === 'lshape' ? d.qr || null : null };
  // QR only on the L-shape strip: the bar (216 px) and the corner are too small to scan.
  if (d.shape === 'lshape') {
    const m = paintLShape(ctx, p, dd);
    // The strip's "scrollHeight <= clientHeight": where the content ends vs the strip's inner bottom (read by tests and the browser checks).
    canvas.layoutInfo = { contentBottom: m.contentBottom, limit: m.limit, overflow: m.overflow, titleSize: m.titleSize, heroD: m.hero ? m.hero.d : 0 };
  }
  else if (d.shape === 'bottom') paintBar(ctx, p, dd);
  else paintCorner(ctx, p, dd);
  return canvas;
}

export function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob returned null (tainted canvas?)'))), 'image/png');
    } catch (e) { reject(e); }
  });
}

export { PALETTES };
